import { AppHealth, HealthStatus } from './models';

export type SortKey = 'worst' | 'name' | 'rate';
export type ViewMode = 'cards' | 'list';

export const SORTS: { value: SortKey; label: string }[] = [
  { value: 'worst', label: 'Worst first' },
  { value: 'name', label: 'Name A to Z' },
  { value: 'rate', label: 'Lowest success rate' },
];

// Worst first, so the cards that need attention are the first thing to filter on.
export const FILTERS: { status: HealthStatus; label: string; always: boolean }[] = [
  { status: 'Unhealthy', label: 'Unhealthy', always: true },
  { status: 'Degraded', label: 'Degraded', always: true },
  { status: 'Healthy', label: 'Healthy', always: true },
  { status: 'NoData', label: 'No data', always: true },
  { status: 'Error', label: 'Error', always: false },
  { status: 'AccessDenied', label: 'No access', always: false },
];

export interface ViewState {
  selected: ReadonlySet<HealthStatus>;
  query: string;
  sort: SortKey;
  view: ViewMode;
  group: string;
}

const STATUS_PARAM = 'status';
const QUERY_PARAM = 'q';
const SORT_PARAM = 'sort';
const VIEW_PARAM = 'view';
const GROUP_PARAM = 'group';

// ?status=Unhealthy&q=kvitter&sort=name&view=list&group=rg-x makes a view shareable and survives a reload.
export function readViewFromUrl(): ViewState {
  const params = new URLSearchParams(window.location.search);
  const known = new Set<string>(FILTERS.map((filter) => filter.status));
  const statuses = (params.get(STATUS_PARAM) ?? '')
    .split(',')
    .filter((value): value is HealthStatus => known.has(value));
  return {
    selected: new Set(statuses),
    query: params.get(QUERY_PARAM) ?? '',
    sort: SORTS.find((sort) => sort.value === params.get(SORT_PARAM))?.value ?? 'worst',
    view: params.get(VIEW_PARAM) === 'list' ? 'list' : 'cards',
    group: params.get(GROUP_PARAM) ?? '',
  };
}

export function writeViewToUrl(state: ViewState): void {
  const url = new URL(window.location.href);
  const set = (name: string, value: string | null): void => {
    if (value) {
      url.searchParams.set(name, value);
    } else {
      url.searchParams.delete(name);
    }
  };
  set(STATUS_PARAM, state.selected.size === 0 ? null : [...state.selected].join(','));
  set(QUERY_PARAM, state.query.trim() === '' ? null : state.query);
  set(SORT_PARAM, state.sort === 'worst' ? null : state.sort);
  set(VIEW_PARAM, state.view === 'list' ? 'list' : null);
  set(GROUP_PARAM, state.group === '' ? null : state.group);
  window.history.replaceState(null, '', url);
}

// Problems first, then what could not be judged, then the healthy ones.
const WORST_FIRST: Record<HealthStatus, number> = {
  Unhealthy: 0,
  Degraded: 1,
  Error: 2,
  AccessDenied: 3,
  NoData: 4,
  Healthy: 5,
};

export function byWorst(a: AppHealth, b: AppHealth): number {
  return WORST_FIRST[a.status] - WORST_FIRST[b.status] || byName(a, b);
}

export function byName(a: AppHealth, b: AppHealth): number {
  const collator = { sensitivity: 'base' } as const;
  return (
    (a.name ?? a.appId).localeCompare(b.name ?? b.appId, undefined, collator) ||
    (a.roleName ?? '').localeCompare(b.roleName ?? '', undefined, collator)
  );
}

export function byRate(a: AppHealth, b: AppHealth): number {
  const left = a.healthPercentage;
  const right = b.healthPercentage;
  if (left === right) {
    return byName(a, b);
  }
  // Apps without a percentage have nothing to rank, so they go last.
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  return left - right || byName(a, b);
}
